/**
 * SCRUM-130/131/133 — Digital Signage hub para el tenant.
 * /app/pantallas — listar / crear / vincular / editar contenido / revocar displays.
 *
 * NO confundir con el hub del Customer Display (caja). Este maneja las
 * pantallas de comunicación al público (menu boards, ofertas, cola).
 *
 * Gate visual: feature flag digital_signage. Backend enforcea can_manage_branding.
 * Nunca usa APIs nativas de confirm/alert/prompt — solo AppDialogs.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { CrudEmptyState, CrudShellHeader, Loading } from "@/components/ds";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Monitor,
  RefreshCw,
  Plus,
  Loader2,
  Trash2,
  KeyRound,
  HelpCircle,
  ExternalLink,
  Copy,
  ImageIcon,
  Tag,
  Ticket,
  ListOrdered,
  Layers,
  Check,
  ChevronDown,
  ChevronRight,
  X,
  Wifi,
  WifiOff,
  Circle,
  Clock,
  CalendarDays,
  AlertTriangle,
} from "lucide-react";
import { useAppDialogs } from "@/components/ui/app-dialogs";
import { SignageContentRenderer } from "@/components/signage/SignageContentRenderer";
import type {
  SignageDisplayRow,
  SignageContent,
  SignageDraft,
  SignageDraftBase,
  SignageDraftPlaylist,
  SignageMenuPerSlide,
  SignageTransition,
} from "@/lib/db-service/signage";
import type { SignageScheduleRule } from "@/lib/db-service/signage-schedules";
import { recommendMenuPerSlide } from "@/lib/signage-menu";
import type { SignageBranding } from "@/lib/signage-branding";

import {
  resolveSignageNicheId,
  getTemplatesForNiche,
  SIGNAGE_NICHE_MATRIX,
  SIGNAGE_TEMPLATES,
  type SignageNicheId,
  type SignageTemplateDef,
} from "@/lib/signage-niche-templates";
import { resolveActiveRule } from "@/lib/signage-schedule";
import { getSupportedAdapters } from "@/lib/signage-adapters";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

export const Route = createFileRoute("/app/pantallas")({
  component: SignageHubPage,
  head: () => ({ meta: [{ title: "Pantallas · Digital Signage" }] }),
});

type ListResult = {
  items: SignageDisplayRow[];
  total: number;
  branding?: SignageBranding & { businessProfile?: string | null };
};

type ProductLite = {
  id: string;
  name: string;
  precioUnitario: number | null;
  precioPorKg: number | null;
  imagenUrl: string | null;
};

type PromotionLite = {
  id: string;
  name: string;
  bannerTitle?: string | null;
  bannerBadge?: string | null;
  active: boolean;
};

type CouponLite = {
  id: string;
  code: string;
  name: string;
  type: "percentage" | "fixed";
  value: number;
  active: boolean;
};

type MediaLite = {
  id: string;
  publicUrl: string;
  thumbnailUrl: string | null;
  originalName: string;
  mimeType: string;
};

const STATUS_LABELS: Record<string, string> = {
  unpaired: "Sin vincular",
  paired: "Vinculada",
  revoked: "Revocada",
};

const STATUS_COLORS: Record<string, string> = {
  unpaired: "bg-yellow-100 text-yellow-800",
  paired: "bg-green-100 text-green-800",
  revoked: "bg-red-100 text-red-800",
};

const ONLINE_THRESHOLD_MS = 60_000;

function isOnline(item: SignageDisplayRow): boolean {
  if (item.status !== "paired") return false;
  if (!item.lastSeenAt) return false;
  const ts = new Date(item.lastSeenAt).getTime();
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts <= ONLINE_THRESHOLD_MS;
}

function copyText(value: string, successMsg: string) {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      void navigator.clipboard
        .writeText(value)
        .then(() => toast.success(successMsg))
        .catch(() => toast.info(value));
      return;
    }
  } catch {
    // fallthrough
  }
  toast.info(value);
}

function formatExpiry(iso: string | null): string {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return "";
  if (ms <= 0) return "expirado";
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "< 1 min";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  return `${h}h ${mins % 60}m`;
}

function SignageHubPage() {
  const tid = useWorkspaceTenantId();
  const { confirm, alert, prompt } = useAppDialogs();
  const [items, setItems] = useState<SignageDisplayRow[]>([]);
  const [branding, setBranding] = useState<SignageBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<"pantallas" | "programacion" | "plantillas" | "ayuda">("pantallas");
  const tenantNiche = useMemo<SignageNicheId>(
    () => resolveSignageNicheId((branding as (SignageBranding & { businessProfile?: string | null }) | null)?.businessProfile ?? null),
    [branding],
  );
  // SCRUM-131: filtro de sucursal
  const [locationFilter, setLocationFilter] = useState<string>("");

  // Fuentes de contenido (se cargan al abrir la primera card editable).
  const [products, setProducts] = useState<ProductLite[] | null>(null);
  const [promotions, setPromotions] = useState<PromotionLite[] | null>(null);
  const [coupons, setCoupons] = useState<CouponLite[] | null>(null);
  const [media, setMedia] = useState<MediaLite[] | null>(null);

  const pairBase =
    typeof window !== "undefined" ? `${window.location.origin}/signage/pair` : "/signage/pair";
  const pairUrlFor = (code?: string | null) =>
    code ? `${pairBase}?code=${encodeURIComponent(code)}` : pairBase;
  const pairUrl = pairBase; // ayuda genérica sin código

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const data = await apiGetJson<ListResult>("/api/signage/displays", tid);
      setItems(data.items);
      if (data.branding) setBranding(data.branding);
    } catch {
      toast.error("Error al cargar pantallas");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    void load();
    // Refresco periódico para actualizar online/offline heurístico.
    const id = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(id);
  }, [load]);

  // Carga perezosa de fuentes cuando se abre un editor.
  const ensureSources = useCallback(async () => {
    if (!tid) return;
    const jobs: Array<Promise<void>> = [];
    if (products === null) {
      jobs.push(
        apiGetJson<{ items: ProductLite[] } | { items: unknown[] } | unknown>(
          "/api/products",
          tid,
        )
          .then((raw) => {
            const items = extractProducts(raw);
            setProducts(items);
          })
          .catch(() => setProducts([])),
      );
    }
    if (promotions === null) {
      jobs.push(
        apiGetJson<{ promotions: PromotionLite[] }>("/api/promotions", tid)
          .then((res) => setPromotions(res.promotions ?? []))
          .catch(() => setPromotions([])),
      );
    }
    if (coupons === null) {
      jobs.push(
        apiGetJson<{ coupons: CouponLite[] }>("/api/coupons", tid)
          .then((res) => setCoupons(res.coupons ?? []))
          .catch(() => setCoupons([])),
      );
    }
    if (media === null) {
      jobs.push(
        apiGetJson<{ items?: MediaLite[]; media?: MediaLite[] }>(
          "/api/media-library?limit=50",
          tid,
        )
          .then((res) => setMedia(res.items ?? res.media ?? []))
          .catch(() => setMedia([])),
      );
    }
    await Promise.allSettled(jobs);
  }, [tid, products, promotions, coupons, media]);

  const handleCreate = async () => {
    if (!tid) return;
    const name = newName.trim();
    if (name.length < 2) {
      toast.error("El nombre debe tener al menos 2 caracteres");
      return;
    }
    setCreating(true);
    try {
      const result = await apiSendJson<{ ok: boolean; item: SignageDisplayRow }>(
        "/api/signage/displays",
        "POST",
        { name },
        tid,
      );
      toast.success("Pantalla creada — usá el código para vincular el player");
      setNewName("");
      setSelectedId(result.item.id);
      void load();
    } catch {
      toast.error("Error al crear la pantalla");
    } finally {
      setCreating(false);
    }
  };

  const handleRegenerate = async (item: SignageDisplayRow) => {
    if (!tid) return;
    const ok = await confirm({
      title: "Generar nuevo código",
      description:
        "El código vigente dejará de funcionar. El player va a tener que volver a vincularse con el nuevo código.",
      confirmLabel: "Generar código nuevo",
    });
    if (!ok) return;
    try {
      const result = await apiSendJson<{ ok: boolean; pairCode: string; pairCodeExpiresAt: string }>(
        "/api/signage/displays",
        "PATCH",
        { id: item.id, action: "regenerate-pair" },
        tid,
      );
      await alert({
        title: "Código generado",
        description: `Nuevo código: ${result.pairCode}\n\nAbrí ${pairUrlFor(result.pairCode)} en el player (o pegá el código). Vale 60 min.`,
      });
      void load();
    } catch {
      toast.error("Error al generar código");
    }
  };

  const handleRevoke = async (item: SignageDisplayRow) => {
    if (!tid) return;
    const ok = await confirm({
      title: "Revocar pantalla",
      description: `La pantalla "${item.name}" quedará revocada y el player dejará de recibir contenido. Podés crear una nueva más tarde.`,
      destructive: true,
      confirmLabel: "Revocar",
    });
    if (!ok) return;
    try {
      await apiSendJson(
        `/api/signage/displays?id=${encodeURIComponent(item.id)}`,
        "DELETE",
        null,
        tid,
      );
      toast.success("Pantalla revocada");
      void load();
    } catch {
      toast.error("Error al revocar");
    }
  };

  // SCRUM-131: renombrar display con AppDialogs (usa prompt de useAppDialogs, nunca el nativo)
  const handleRename = async (item: SignageDisplayRow) => {
    if (!tid) return;
    const newName = await prompt({
      title: "Renombrar pantalla",
      description: `Nombre actual: "${item.name}"`,
      placeholder: item.name,
      confirmLabel: "Renombrar",
      validate: (v) =>
        v.trim().length < 2
          ? "Mínimo 2 caracteres"
          : v.trim().length > 200
            ? "Máximo 200 caracteres"
            : null,
    });
    if (newName == null) return; // cancelado
    const trimmed = newName.trim();
    if (!trimmed || trimmed === item.name) return;
    try {
      await apiSendJson(
        "/api/signage/displays",
        "PATCH",
        { id: item.id, action: "rename", name: trimmed },
        tid,
      );
      toast.success("Pantalla renombrada");
      void load();
    } catch {
      toast.error("Error al renombrar");
    }
  };

  const handleSetDraft = async (item: SignageDisplayRow, draft: SignageDraft) => {
    if (!tid) return;
    try {
      await apiSendJson(
        "/api/signage/displays",
        "PATCH",
        { id: item.id, action: "set-content", draft },
        tid,
      );
      toast.success("Contenido actualizado");
      void load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error al actualizar contenido";
      toast.error(msg);
    }
  };

  const grouped = useMemo(() => {
    const filtered = locationFilter
      ? items.filter((i) => i.locationId === locationFilter)
      : items;
    const active = filtered.filter((i) => i.status !== "revoked");
    const revoked = filtered.filter((i) => i.status === "revoked");
    return { active, revoked };
  }, [items, locationFilter]);

  // IDs únicos de sucursales presentes en displays (para el filtro)
  const locationIds = useMemo(() => {
    const ids = new Set<string>();
    for (const it of items) {
      if (it.locationId) ids.add(it.locationId);
    }
    return Array.from(ids);
  }, [items]);

  if (loading && items.length === 0) return <Loading />;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <CrudShellHeader
        title="Pantallas · Digital Signage"
        subtitle="Menu boards, ofertas, colas de turno. No confundir con el Display del cliente en caja."
      />
      <MerchantExperienceBar screenId="pantallas" />

      {/* ── SCRUM-144: Sub-nav tabs ────────────────────────────────────────── */}
      <div className="flex gap-1 overflow-x-auto border-b pb-0">
        {(
          [
            { id: "pantallas", label: "Pantallas" },
            { id: "programacion", label: "Programación" },
            { id: "plantillas", label: "Plantillas" },
            { id: "ayuda", label: "Ayuda" },
          ] as const
        ).map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Ayuda tab ──────────────────────────────────────────────────────── */}
      {activeTab === "ayuda" && (
      <Card>
        <CardHeader
          className="cursor-pointer"
          onClick={() => setHelpOpen((v) => !v)}
        >
          <div className="flex items-center gap-2 text-sm font-semibold">
            <HelpCircle className="h-4 w-4" />
            Cómo configurar una pantalla
            {helpOpen ? (
              <ChevronDown className="ml-auto h-4 w-4" />
            ) : (
              <ChevronRight className="ml-auto h-4 w-4" />
            )}
          </div>
        </CardHeader>
        {helpOpen ? (
          <CardContent className="space-y-4 text-sm">
            <ol className="list-decimal space-y-3 pl-5">
              <li>
                <strong>Crear pantalla</strong> abajo, con un nombre humano
                (ej: <em>&ldquo;Menu Board Barra&rdquo;</em>).
              </li>
              <li>
                <strong>En la TV / tablet / mini-PC</strong> abrir en el
                navegador la URL de vinculación:
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <code className="rounded bg-muted px-2 py-1 font-mono text-xs">
                    {pairUrl}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyText(pairUrl, "URL copiada")}
                  >
                    <Copy className="mr-1 h-3.5 w-3.5" /> Copiar URL
                  </Button>
                  <Button size="sm" variant="ghost" asChild>
                    <a href={pairUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1 h-3.5 w-3.5" /> Abrir
                    </a>
                  </Button>
                </div>
              </li>
              <li>
                <strong>Ingresar el código</strong> corto (6 caracteres) que
                aparece en la card de la pantalla. Dura 60 minutos; si
                expira, generá uno nuevo desde aquí.
              </li>
              <li>
                <strong>Elegir el contenido</strong> desde &ldquo;Cambiar
                contenido&rdquo;: <em>Idle</em> · Texto · Imagen (de tu Media
                Library) · Menú de productos · Promoción · Cupón · Playlist
                (rotación con transición).
              </li>
              <li>
                Podés <strong>revocar</strong> la pantalla en cualquier
                momento — el player deja de recibir contenido.
              </li>
            </ol>
            <div className="rounded-md border border-amber-300/50 bg-amber-50/50 p-3 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              <strong>≠ Display del cliente en caja.</strong> Esta pantalla es
              para <em>comunicación al público</em> (menu, ofertas). El
              display del cliente que muestra el ticket durante el cobro se
              vincula desde el POS (/display/pair).
            </div>
          </CardContent>
        ) : null}
      </Card>
      )}

      {/* ── Pantallas tab ─────────────────────────────────────────────────── */}
      {activeTab === "pantallas" && (
      <>
      {/* Crear pantalla ---------------------------------------------------- */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Plus className="h-4 w-4" /> Nueva pantalla
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="min-w-64 flex-1">
            <label className="mb-1 block text-xs font-medium">Nombre</label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder='Ej: "Menu Board Barra"'
              maxLength={200}
            />
          </div>
          <Button onClick={handleCreate} disabled={creating || newName.trim().length < 2}>
            {creating ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-1.5 h-4 w-4" />
            )}
            Crear
          </Button>
          {locationIds.length > 0 && (
            <div className="min-w-40">
              <label className="mb-1 block text-xs font-medium">Sucursal</label>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todas</SelectItem>
                  {locationIds.map((lid) => (
                    <SelectItem key={lid} value={lid}>
                      {lid}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </CardContent>
      </Card>

      {grouped.active.length === 0 ? (
        <CrudEmptyState
          icon={Monitor}
          title="Sin pantallas configuradas"
          description='Creá tu primera pantalla arriba y seguí los pasos de "Cómo configurar" para vincular una TV o tablet.'
        />
      ) : (
        <div className="space-y-3">
          {grouped.active.map((item) => {
            const online = isOnline(item);
            const isOpen = selectedId === item.id;
            return (
              <Card
                key={item.id}
                className={`transition-shadow ${isOpen ? "ring-2 ring-primary" : ""}`}
              >
                <CardHeader
                  className="cursor-pointer pb-2"
                  onClick={() => setSelectedId(isOpen ? null : item.id)}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    <span className="font-semibold">{item.name}</span>
                    <Badge className={STATUS_COLORS[item.status]}>
                      {STATUS_LABELS[item.status]}
                    </Badge>
                    {item.status === "paired" ? (
                      <Badge
                        variant="outline"
                        className={
                          online
                            ? "border-emerald-500 text-emerald-600"
                            : "border-slate-400 text-slate-500"
                        }
                      >
                        {online ? (
                          <Wifi className="mr-1 h-3 w-3" />
                        ) : (
                          <WifiOff className="mr-1 h-3 w-3" />
                        )}
                        {online ? "Online" : "Offline"}
                      </Badge>
                    ) : null}
                    <Badge variant="outline" className="text-xs">
                      <Circle className="mr-1 h-2 w-2" fill="currentColor" />
                      {kindLabel(item.currentContent.kind)}
                    </Badge>
                    <span className="ml-auto text-xs text-muted-foreground flex flex-col items-end gap-0.5">
                      {item.lastSeenAt
                        ? `Último ping: ${new Date(item.lastSeenAt).toLocaleString("es-AR")}`
                        : "Nunca vinculada"}
                      {/* SCRUM-141: last_sync_at — última entrega de contenido */}
                      {item.lastSyncAt ? (
                        <span className="text-[11px] text-muted-foreground/70">
                          {`Último sync: ${new Date(item.lastSyncAt).toLocaleString("es-AR")}`}
                        </span>
                      ) : null}
                    </span>
                  </div>
                </CardHeader>
                {isOpen ? (
                  <CardContent>
                    <SignageCardBody
                      item={item}
                      branding={branding}
                      pairUrl={pairUrlFor(item.pairCode)}
                      products={products}
                      promotions={promotions}
                      coupons={coupons}
                      media={media}
                      tenantNiche={tenantNiche}
                      onEnsureSources={ensureSources}
                      onRegenerate={() => void handleRegenerate(item)}
                      onRevoke={() => void handleRevoke(item)}
                      onApplyDraft={(draft) => void handleSetDraft(item, draft)}
                      onCopyCode={(c) => copyText(c, "Código copiado")}
                      onCopyPairUrl={() => copyText(pairUrlFor(item.pairCode), "URL de vinculación copiada")}
                      onPromptRename={() => void handleRename(item)}
                    />
                  </CardContent>
                ) : null}
              </Card>
            );
          })}
        </div>
      )}

      {grouped.revoked.length > 0 ? (
        <details className="rounded-md border p-3 text-sm text-muted-foreground">
          <summary className="cursor-pointer">
            Pantallas revocadas ({grouped.revoked.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {grouped.revoked.map((r) => (
              <li key={r.id}>
                <span className="line-through">{r.name}</span> · revocada{" "}
                {r.revokedAt ? new Date(r.revokedAt).toLocaleDateString("es-AR") : ""}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      </>
      )}

      {/* ── Programación tab ──────────────────────────────────────────────── */}
      {activeTab === "programacion" && tid ? (
        <ProgramacionOverview items={grouped.active} tenantId={tid} />
      ) : null}

      {/* ── Plantillas tab ────────────────────────────────────────────────── */}
      {activeTab === "plantillas" ? (
        <TemplateGallery niche={tenantNiche} />
      ) : null}
    </div>
  );
}


// ─── SCRUM-140: Temporal Preview ─────────────────────────────────────────────

function TemporalPreview({ rules }: { rules: SignageScheduleRule[] }) {
  const published = rules.filter((r) => r.status === "published" && r.content != null);
  const [previewAt, setPreviewAt] = useState<string>(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  });
  const [result, setResult] = useState<ReturnType<typeof resolveActiveRule> | null>(null);
  const [ran, setRan] = useState(false);

  const runPreview = () => {
    const date = new Date(previewAt);
    if (!Number.isFinite(date.getTime())) return;
    const winner = resolveActiveRule(published, date);
    setResult(winner ?? null);
    setRan(true);
  };

  return (
    <div className="rounded-md border bg-muted/10 p-3">
      <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
        Vista previa temporal — ¿qué se ve a las X?
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs">
          Fecha y hora
          <Input
            type="datetime-local"
            className="mt-1 h-8 text-xs w-52"
            value={previewAt}
            onChange={(e) => { setPreviewAt(e.target.value); setRan(false); }}
          />
        </label>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={runPreview} disabled={published.length === 0}>
          <Clock className="mr-1 h-3 w-3" />
          Vista previa a esta hora
        </Button>
      </div>
      {published.length === 0 ? (
        <p className="mt-2 text-[11px] text-muted-foreground">Sin reglas publicadas — se muestra el contenido estático del display.</p>
      ) : null}
      {ran && (
        <div className="mt-2 rounded-md border bg-background p-2 text-xs">
          {result ? (
            <>
              <p className="font-medium text-emerald-600">
                Regla activa: &ldquo;{result.label}&rdquo;
              </p>
              <p className="text-muted-foreground">
                Prioridad efectiva: {result.priority} · Contenido:{" "}
                <span className="font-mono">{result.content?.kind ?? "—"}</span>
                {result.scope === "display" ? " (regla de display)" : " (regla de sucursal)"}
              </p>
              <div className="mt-1">
                <p className="font-medium">Dónde publicada:</p>
                {published.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <CalendarDays className="h-3 w-3 shrink-0" />
                    <span>{r.label}</span>
                    <Badge
                      variant="outline"
                      className={r.status === "published" ? "text-[9px] bg-green-50 text-green-700" : "text-[9px]"}
                    >
                      {r.status}
                    </Badge>
                    {r.locationId ? <span className="text-[10px]">sucursal: {r.locationId}</span> : null}
                    {r.displayId ? <span className="text-[10px]">display: {r.displayId.slice(-6)}</span> : null}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-muted-foreground">
              Ninguna regla activa a esa hora — el display muestra su contenido estático.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SCRUM-144: Template Gallery (Plantillas tab) ─────────────────────────────

function TemplateGallery({ niche }: { niche: SignageNicheId }) {
  const allNiches = Object.keys(SIGNAGE_NICHE_MATRIX) as SignageNicheId[];
  const [activeNiche, setActiveNiche] = useState<SignageNicheId>(niche);
  const templates = getTemplatesForNiche(activeNiche);

  const NICHE_LABELS: Record<SignageNicheId, string> = {
    food: "Gastronomía",
    retail: "Retail / Comercio",
    services: "Servicios",
    generic: "Genérico",
  };

  return (
    <div className="space-y-4 rounded-md border p-4">
      <div>
        <p className="text-sm font-semibold">Plantillas disponibles por nicho</p>
        <p className="text-xs text-muted-foreground">
          Las plantillas configuran el modo de contenido y los hints de layout para la pantalla.
          El player es agnóstico al nicho — solo cambian los defaults del editor.
        </p>
      </div>

      {/* Niche selector */}
      <div className="flex flex-wrap gap-2">
        {allNiches.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setActiveNiche(n)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              activeNiche === n
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:border-primary/60"
            }`}
          >
            {NICHE_LABELS[n]}
            {n === niche ? " ✓" : ""}
          </button>
        ))}
      </div>

      {/* Template grid */}
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {templates.map((tpl) => (
          <div
            key={tpl.id}
            className="rounded-md border bg-card p-3 space-y-1.5"
          >
            <div className="flex items-start justify-between gap-1">
              <p className="text-sm font-semibold leading-tight">{tpl.label}</p>
              {tpl.foodRich ? (
                <Badge variant="outline" className="text-[10px] shrink-0">
                  Gastro
                </Badge>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground leading-snug">{tpl.description}</p>
            <div className="flex flex-wrap gap-1 pt-1">
              {tpl.orientations.map((o) => (
                <Badge key={o} variant="secondary" className="text-[10px]">
                  {o === "landscape" ? "Horizontal" : "Vertical"}
                </Badge>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Modo por defecto: <span className="font-mono">{tpl.defaultDraftMode}</span>
            </p>
            <p className="text-[10px] text-muted-foreground">
              {tpl.recommendedResolutions.join(" · ")}
            </p>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground border-t pt-3">
        Nicho activo del tenant:{" "}
        <span className="font-semibold">{NICHE_LABELS[niche]}</span>
        {" — "}las plantillas de tu nicho aparecen en el editor de contenido de cada pantalla.
        La capabilidad requerida es <span className="font-mono">digital_signage</span> (plan Business).
      </p>
    </div>
  );
}

// ─── SCRUM-144: Programación overview tab ────────────────────────────────────

function ProgramacionOverview({
  items,
  tenantId,
}: {
  items: SignageDisplayRow[];
  tenantId: string;
}) {
  const [allRules, setAllRules] = useState<Array<SignageScheduleRule & { displayName: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId || items.length === 0) return;
    setLoading(true);
    Promise.allSettled(
      items.map((d) =>
        apiGetJson<{ items: SignageScheduleRule[] }>(
          `/api/signage/schedules?displayId=${encodeURIComponent(d.id)}`,
          tenantId,
        ).then((res) =>
          (res.items ?? []).map((r) => ({ ...r, displayName: d.name })),
        ),
      ),
    )
      .then((results) => {
        const flat = results.flatMap((r) => (r.status === "fulfilled" ? r.value : []));
        // Sort by display name then priority desc
        flat.sort((a, b) => a.displayName.localeCompare(b.displayName) || b.priority - a.priority);
        setAllRules(flat);
      })
      .finally(() => setLoading(false));
  }, [items, tenantId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando reglas de todas las pantallas…
      </div>
    );
  }

  if (allRules.length === 0) {
    return (
      <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">
        <CalendarDays className="mx-auto mb-3 h-8 w-8 opacity-30" />
        <p className="font-medium">Sin reglas de programación</p>
        <p className="mt-1 text-xs">
          Creá reglas de horario desde cada pantalla en la pestaña &ldquo;Pantallas&rdquo;.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {allRules.length} regla{allRules.length === 1 ? "" : "s"} en {items.length} pantalla{items.length === 1 ? "" : "s"}.
      </p>
      {allRules.map((rule) => (
        <div
          key={rule.id}
          className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2 text-xs"
        >
          <Monitor className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="font-medium text-muted-foreground">{rule.displayName}</span>
          <span className="font-semibold">{rule.label}</span>
          <span className="text-muted-foreground">
            {rule.startTime}–{rule.endTime}
          </span>
          <Badge variant="outline" className="text-[10px]">P:{rule.priority}</Badge>
          <Badge
            className={
              rule.status === "published"
                ? "bg-green-100 text-green-800 text-[10px]"
                : "bg-yellow-100 text-yellow-800 text-[10px]"
            }
          >
            {rule.status === "published" ? "Publicada" : "Borrador"}
          </Badge>
          {rule.content ? (
            <Badge variant="outline" className="text-[10px]">{rule.content.kind}</Badge>
          ) : null}
          {rule.scope === "location" ? (
            <Badge variant="secondary" className="text-[10px]">Sucursal</Badge>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function kindLabel(kind: SignageContent["kind"]): string {
  switch (kind) {
    case "idle":
      return "Idle";
    case "text":
      return "Texto";
    case "image":
      return "Imagen";
    case "url":
      return "URL";
    case "menu":
      return "Menú";
    case "promo":
      return "Promo";
    case "coupon":
      return "Cupón";
    case "playlist":
      return "Playlist";
    default:
      return String(kind);
  }
}

function extractProducts(raw: unknown): ProductLite[] {
  if (!raw) return [];
  // /api/products puede devolver Array<Row> o { items, total }
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as { items?: unknown }).items)
      ? ((raw as { items: unknown[] }).items as unknown[])
      : [];
  return list
    .map((r) => {
      if (!r || typeof r !== "object") return null;
      const rec = r as Record<string, unknown>;
      const id = rec.id != null ? String(rec.id) : null;
      const name = rec.name != null ? String(rec.name) : null;
      if (!id || !name) return null;
      return {
        id,
        name,
        precioUnitario:
          rec.precioUnitario != null ? Number(rec.precioUnitario) : null,
        precioPorKg: rec.precioPorKg != null ? Number(rec.precioPorKg) : null,
        imagenUrl: rec.imagenUrl != null ? String(rec.imagenUrl) : null,
      } satisfies ProductLite;
    })
    .filter((p): p is ProductLite => p != null);
}

// ─── Card body ────────────────────────────────────────────────────────────────

function SignageCardBody({
  item,
  branding,
  pairUrl,
  products,
  promotions,
  coupons,
  media,
  tenantNiche = "generic" as SignageNicheId,
  onEnsureSources,
  onRegenerate,
  onRevoke,
  onApplyDraft,
  onCopyCode,
  onCopyPairUrl,
  onPromptRename,
}: {
  item: SignageDisplayRow;
  branding: SignageBranding | null;
  pairUrl: string;
  products: ProductLite[] | null;
  promotions: PromotionLite[] | null;
  coupons: CouponLite[] | null;
  media: MediaLite[] | null;
  tenantNiche?: SignageNicheId;
  onEnsureSources: () => Promise<void>;
  onRegenerate: () => void;
  onRevoke: () => void;
  onApplyDraft: (draft: SignageDraft) => void;
  onCopyCode: (code: string) => void;
  onCopyPairUrl: () => void;
  onPromptRename: () => void;
}) {
  useEffect(() => {
    void onEnsureSources();
  }, [onEnsureSources]);

  return (
    <div className="space-y-5">
      {/* Vinculación ---------------------------------------------------- */}
      <div className="rounded-md border bg-muted/20 p-3">
        <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
          Vinculación
        </p>
        {item.status === "unpaired" && item.pairCode ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <code className="rounded-md border bg-background px-4 py-2 font-mono text-3xl font-bold tracking-widest">
                {item.pairCode}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCopyCode(item.pairCode!)}
              >
                <Copy className="mr-1.5 h-4 w-4" /> Copiar código
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <a href={pairUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-1.5 h-4 w-4" /> Abrir vinculación
                </a>
              </Button>
              <Button variant="outline" size="sm" onClick={onCopyPairUrl}>
                <Copy className="mr-1.5 h-4 w-4" /> Copiar URL
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Abrí la URL de vinculación (incluye el código) en la TV / tablet, o pegá el código en /signage/pair.
              {item.pairCodeExpiresAt
                ? formatExpiry(item.pairCodeExpiresAt) === "expirado"
                  ? " Código vencido — tocá «Nuevo código de vinculación»."
                  : ` Expira en ${formatExpiry(item.pairCodeExpiresAt)}.`
                : ""}
            </p>
          </div>
        ) : item.status === "paired" ? (
          <div className="space-y-1 text-sm">
            <p className="font-medium text-emerald-600">
              <Check className="mr-1 inline h-4 w-4" /> Player vinculado
            </p>
            <p className="text-xs text-muted-foreground">
              El token del player no se muestra por seguridad — vive en el
              propio dispositivo. Si el player se perdió o se reseteó,
              regenerá el código de vinculación abajo.
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Sin código activo.</p>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onRegenerate}>
            <KeyRound className="mr-1.5 h-4 w-4" />
            Nuevo código de vinculación
          </Button>
          <Button variant="ghost" size="sm" onClick={onPromptRename}>
            Renombrar
          </Button>
          <Button variant="destructive" size="sm" onClick={onRevoke}>
            <Trash2 className="mr-1.5 h-4 w-4" />
            Revocar
          </Button>
        </div>
      </div>

      {/* Contenido actual + preview ------------------------------------ */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-md border bg-muted/20 p-3">
          <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
            Contenido actual
          </p>
          <ContentSummary content={item.currentContent} />
        </div>
        <div className="overflow-hidden rounded-md border" style={{ backgroundColor: branding?.backgroundColor ?? "#0c1220" }}>
          <p className="border-b border-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider opacity-70 text-white">
            Preview
          </p>
          <div className="flex h-48 flex-col overflow-hidden">
            <SignageContentRenderer
              content={item.currentContent}
              name={item.name}
              branding={branding}
              compact
              refreshKey={item.updatedAt}
            />
          </div>
        </div>
      </div>

      {/* Editor -------------------------------------------------------- */}
      <ContentEditor
        current={item.currentContent}
        products={products}
        promotions={promotions}
        coupons={coupons}
        media={media}
        onApply={onApplyDraft}
        tenantNiche={tenantNiche}
      />

      {/* SCRUM-135: Programación por franja horaria -------------------- */}
      <ScheduleSection displayId={item.id} tenantId={item.tenantId} />
    </div>
  );
}

function ContentSummary({ content }: { content: SignageContent }) {
  const chip = (
    <Badge variant="outline" className="text-xs">
      {kindLabel(content.kind)}
    </Badge>
  );
  switch (content.kind) {
    case "idle":
      return (
        <div className="flex items-center gap-2 text-sm">
          {chip}
          <span className="text-muted-foreground">Sin contenido asignado.</span>
        </div>
      );
    case "text":
      return (
        <div className="flex items-start gap-2 text-sm">
          {chip}
          <span className="line-clamp-3">&ldquo;{content.text}&rdquo;</span>
        </div>
      );
    case "image":
      return (
        <div className="flex items-start gap-2 text-sm">
          {chip}
          <span className="line-clamp-1 break-all text-xs text-muted-foreground">
            {content.url}
          </span>
        </div>
      );
    case "url":
      return (
        <div className="flex items-start gap-2 text-sm">
          {chip}
          <span className="line-clamp-1 break-all text-xs text-muted-foreground">
            {content.url}
          </span>
        </div>
      );
    case "menu":
      return (
        <div className="flex items-start gap-2 text-sm">
          {chip}
          <span>
            {content.items.length} producto{content.items.length === 1 ? "" : "s"} ·{" "}
            {content.layout ?? "grid"}
            {content.title ? ` · ${content.title}` : ""}
          </span>
        </div>
      );
    case "promo":
      return (
        <div className="flex items-start gap-2 text-sm">
          {chip}
          <span>{content.item.name}</span>
        </div>
      );
    case "coupon":
      return (
        <div className="flex items-start gap-2 text-sm">
          {chip}
          <span className="font-mono">{content.item.code}</span>
          <span className="text-muted-foreground">· {content.item.discountLabel}</span>
        </div>
      );
    case "playlist":
      return (
        <div className="flex items-start gap-2 text-sm">
          {chip}
          <span>
            {content.slides.length} slides · dwell {content.dwellMs}ms · transición{" "}
            {content.transition.type}
          </span>
        </div>
      );
    default:
      return chip;
  }
}

// ─── Editor ───────────────────────────────────────────────────────────────────

type EditorMode = SignageDraftBase["mode"] | "playlist";

const EDITOR_MODES: Array<{ value: EditorMode; label: string; icon: typeof Monitor }> = [
  { value: "idle", label: "Idle (espera)", icon: Circle },
  { value: "text", label: "Texto grande", icon: Circle },
  { value: "image", label: "Imagen (Media Library)", icon: ImageIcon },
  { value: "url", label: "Página web (URL)", icon: ExternalLink },
  { value: "menu", label: "Menú de productos", icon: ListOrdered },
  { value: "promo", label: "Promoción", icon: Tag },
  { value: "coupon", label: "Cupón", icon: Ticket },
  { value: "playlist", label: "Playlist (rotación)", icon: Layers },
];

function ContentEditor({
  current,
  products,
  promotions,
  coupons,
  media,
  onApply,
  tenantNiche = "generic",
}: {
  current: SignageContent;
  products: ProductLite[] | null;
  promotions: PromotionLite[] | null;
  coupons: CouponLite[] | null;
  media: MediaLite[] | null;
  onApply: (draft: SignageDraft) => void;
  tenantNiche?: SignageNicheId;
}) {
  const [mode, setMode] = useState<EditorMode>(
    current.kind === "playlist" ? "playlist" : (current.kind as EditorMode),
  );
  const [selectedTemplate, setSelectedTemplate] = useState<SignageTemplateDef | null>(null);

  const nicheTemplates = useMemo(() => getTemplatesForNiche(tenantNiche), [tenantNiche]);

  const applyTemplate = (tpl: SignageTemplateDef) => {
    setSelectedTemplate(tpl);
    setMode(tpl.defaultDraftMode as EditorMode);
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      {/* SCRUM-134: template picker by niche */}
      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase text-muted-foreground">
          Plantilla de contenido (nicho: {tenantNiche})
        </p>
        <div className="flex flex-wrap gap-2">
          {nicheTemplates.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              onClick={() => applyTemplate(tpl)}
              className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                selectedTemplate?.id === tpl.id
                  ? "border-primary bg-primary/10 text-primary font-medium"
                  : "border-border hover:border-primary/60 text-muted-foreground hover:text-foreground"
              }`}
              title={tpl.description}
            >
              {tpl.label}
            </button>
          ))}
        </div>
        {selectedTemplate ? (
          <p className="mt-1 text-[11px] text-muted-foreground">
            {selectedTemplate.description}
            {selectedTemplate.foodRich ? " · Optimizada para gastronomía." : ""}
          </p>
        ) : null}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase text-muted-foreground">
          Cambiar contenido
        </p>
        <Select value={mode} onValueChange={(v) => setMode(v as EditorMode)}>
          <SelectTrigger className="w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EDITOR_MODES.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {mode === "playlist" ? (
        <PlaylistEditor
          current={current}
          products={products}
          promotions={promotions}
          coupons={coupons}
          media={media}
          onApply={onApply}
        />
      ) : (
        <BaseEditor
          mode={mode}
          current={current}
          products={products}
          promotions={promotions}
          coupons={coupons}
          media={media}
          onApply={(draft) => onApply(draft)}
        />
      )}
    </div>
  );
}

// ─── Base editor (single kind) ────────────────────────────────────────────────

function baseInitialState(
  mode: Exclude<EditorMode, "playlist">,
  current: SignageContent,
): SignageDraftBase {
  const c = current.kind === mode ? current : null;
  switch (mode) {
    case "idle":
      return { mode: "idle" };
    case "text":
      return {
        mode: "text",
        text: c && c.kind === "text" ? c.text : "",
        theme: c && c.kind === "text" ? c.theme : "dark",
      };
    case "image":
      return { mode: "image", mediaId: null, imageAlt: "" };
    case "url":
      return { mode: "url", externalUrl: c && c.kind === "url" ? c.url : "" };
    case "menu": {
      const p = c && c.kind === "menu" ? c.presentation : undefined;
      return {
        mode: "menu",
        productIds: c && c.kind === "menu" ? c.items.map((it) => it.id) : [],
        menuTitle: c && c.kind === "menu" ? c.title ?? "" : "",
        menuLayout: c && c.kind === "menu" ? c.layout ?? "grid" : "grid",
        menuPerSlide: p?.perSlide,
        menuShowBoardTitle: p?.showBoardTitle,
        menuShowName: p?.showName,
        menuShowPrice: p?.showPrice,
        menuNamePosition: p?.namePosition,
        menuPriceMancheta: p?.priceMancheta,
        menuSlideDwellMs: p?.slideDwellMs,
        menuImageRotateMs: p?.imageRotateMs,
      };
    }
    case "promo":
      return { mode: "promo", promotionId: c && c.kind === "promo" ? c.item.id : null };
    case "coupon":
      return { mode: "coupon", couponId: c && c.kind === "coupon" ? c.item.id : null };
    default: {
      // Exhaustive check
      return { mode: "idle" };
    }
  }
}

/**
 * BaseEditor uncontrolled — mantiene su propio state y expone un botón Aplicar.
 * Se usa para el modo top-level del hub (sin playlist).
 */
function BaseEditor({
  mode,
  current,
  products,
  promotions,
  coupons,
  media,
  onApply,
}: {
  mode: Exclude<EditorMode, "playlist">;
  current: SignageContent;
  products: ProductLite[] | null;
  promotions: PromotionLite[] | null;
  coupons: CouponLite[] | null;
  media: MediaLite[] | null;
  onApply: (draft: SignageDraftBase) => void;
}) {
  const [draft, setDraft] = useState<SignageDraftBase>(() => baseInitialState(mode, current));

  useEffect(() => {
    setDraft(baseInitialState(mode, current));
  }, [mode, current]);

  return (
    <div className="space-y-3">
      <BaseEditorFields
        draft={draft}
        onChange={setDraft}
        products={products}
        promotions={promotions}
        coupons={coupons}
        media={media}
      />
      <div className="flex justify-end">
        <Button size="sm" onClick={() => onApply(draft)} disabled={!draftIsApplyable(draft)}>
          <Check className="mr-1.5 h-4 w-4" />
          Aplicar
        </Button>
      </div>
    </div>
  );
}

/**
 * BaseEditorFields — versión controlada, solo renderiza los campos según el
 * `draft.mode` actual. Reutilizada por el editor top-level y por cada slide
 * de la playlist (donde el parent maneja el state agregado).
 */
function BaseEditorFields({
  draft,
  onChange,
  products,
  promotions,
  coupons,
  media,
  fieldId = "main",
}: {
  draft: SignageDraftBase;
  onChange: (next: SignageDraftBase) => void;
  products: ProductLite[] | null;
  promotions: PromotionLite[] | null;
  coupons: CouponLite[] | null;
  media: MediaLite[] | null;
  /** Unique id so radio groups don't clash across playlist slides. */
  fieldId?: string;
}) {
  const mode = draft.mode;
  return (
    <div className="space-y-3">
      {mode === "idle" ? (
        <p className="text-sm text-muted-foreground">
          Pantalla en espera. Ideal para intervalos sin promo activa.
        </p>
      ) : null}

      {mode === "text" && draft.mode === "text" ? (
        <div className="space-y-2">
          <Textarea
            value={draft.text ?? ""}
            onChange={(e) => onChange({ ...draft, text: e.target.value })}
            rows={2}
            maxLength={500}
            placeholder="Mensaje para mostrar (título grande)"
          />
          <div className="flex items-center gap-3 text-xs">
            <span className="text-muted-foreground">Tema:</span>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name={`theme-${fieldId}`}
                checked={draft.theme !== "light"}
                onChange={() => onChange({ ...draft, theme: "dark" })}
              />
              Oscuro
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name={`theme-${fieldId}`}
                checked={draft.theme === "light"}
                onChange={() => onChange({ ...draft, theme: "light" })}
              />
              Claro
            </label>
          </div>
        </div>
      ) : null}

      {mode === "image" && draft.mode === "image" ? (
        <MediaPicker
          media={media}
          selectedId={draft.mediaId ?? null}
          onChange={(id) => onChange({ ...draft, mediaId: id })}
        />
      ) : null}

      {mode === "url" && draft.mode === "url" ? (
        <div className="space-y-2">
          <Input
            value={draft.externalUrl ?? ""}
            onChange={(e) => onChange({ ...draft, externalUrl: e.target.value })}
            placeholder="https://…"
          />
          <p className="text-xs text-muted-foreground">
            Se muestra dentro de un iframe. Usá esta opción para dashboards o
            menús web hosteados en otra parte.
          </p>
        </div>
      ) : null}

      {mode === "menu" && draft.mode === "menu" ? (
        <MenuEditor
          products={products}
          draft={draft as SignageDraftBase & { mode: "menu" }}
          onChange={(next) => onChange(next)}
        />
      ) : null}

      {mode === "promo" && draft.mode === "promo" ? (
        <PromoPicker
          promotions={promotions}
          selectedId={draft.promotionId ?? null}
          onChange={(id) => onChange({ ...draft, promotionId: id })}
        />
      ) : null}

      {mode === "coupon" && draft.mode === "coupon" ? (
        <CouponPicker
          coupons={coupons}
          selectedId={draft.couponId ?? null}
          onChange={(id) => onChange({ ...draft, couponId: id })}
        />
      ) : null}
    </div>
  );
}

function draftIsApplyable(draft: SignageDraftBase): boolean {
  switch (draft.mode) {
    case "idle":
      return true;
    case "text":
      return !!draft.text?.trim();
    case "image":
      return !!draft.mediaId;
    case "url":
      return !!draft.externalUrl?.trim();
    case "menu":
      return (draft.productIds ?? []).length > 0;
    case "promo":
      return !!draft.promotionId;
    case "coupon":
      return !!draft.couponId;
    default:
      return false;
  }
}

// ─── Pickers ──────────────────────────────────────────────────────────────────

function MediaPicker({
  media,
  selectedId,
  onChange,
}: {
  media: MediaLite[] | null;
  selectedId: string | null;
  onChange: (id: string | null) => void;
}) {
  if (media === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando imágenes…
      </p>
    );
  }
  const images = media.filter((m) => m.mimeType.startsWith("image/"));
  if (!images.length) {
    return (
      <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
        <ImageIcon className="mx-auto mb-2 h-6 w-6 opacity-40" />
        <p>Todavía no hay imágenes en tu Media Library.</p>
        <Button variant="outline" size="sm" className="mt-3" asChild>
          <a href="/app/media">
            <ExternalLink className="mr-1 h-3.5 w-3.5" />
            Subir en Media Library
          </a>
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="max-h-64 overflow-y-auto rounded-md border">
        <div className="grid grid-cols-3 gap-2 p-2 sm:grid-cols-4 md:grid-cols-6">
          {images.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => onChange(m.id)}
              className={`group relative aspect-square overflow-hidden rounded border transition ${selectedId === m.id ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary/60"}`}
              title={m.originalName}
            >
              <img
                src={m.thumbnailUrl ?? m.publicUrl}
                alt={m.originalName}
                className="h-full w-full object-cover"
                loading="lazy"
              />
              {selectedId === m.id ? (
                <span className="absolute right-1 top-1 rounded-full bg-primary p-1 text-primary-foreground">
                  <Check className="h-3 w-3" />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          {selectedId ? "Imagen seleccionada." : "Elegí una imagen."}
        </span>
        <Button variant="ghost" size="sm" asChild>
          <a href="/app/media" target="_blank" rel="noreferrer">
            <ExternalLink className="mr-1 h-3 w-3" />
            Subir en Media Library
          </a>
        </Button>
      </div>
    </div>
  );
}

const PER_SLIDE_OPTIONS: Array<{ value: SignageMenuPerSlide; label: string }> = [
  { value: 1, label: "1 — imagen completa" },
  { value: 2, label: "2 — media pantalla c/u" },
  { value: 3, label: "3 — tercios" },
  { value: 4, label: "4 — cuadrantes (2×2)" },
  { value: 6, label: "6 — grilla 3×2" },
];

function MenuEditor({
  products,
  draft,
  onChange,
}: {
  products: ProductLite[] | null;
  draft: SignageDraftBase & { mode: "menu" };
  onChange: (next: SignageDraftBase) => void;
}) {
  const [filter, setFilter] = useState("");
  const [perSlideTouched, setPerSlideTouched] = useState(draft.menuPerSlide != null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  if (products === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando productos…
      </p>
    );
  }
  if (!products.length) {
    return (
      <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
        <p>No hay productos cargados en el catálogo.</p>
        <Button variant="outline" size="sm" className="mt-3" asChild>
          <a href="/app/productos">Ir al catálogo</a>
        </Button>
      </div>
    );
  }

  const selectedSet = new Set(draft.productIds ?? []);
  const filtered = filter.trim()
    ? products.filter((p) => p.name.toLowerCase().includes(filter.toLowerCase()))
    : products;
  const selectedList = products.filter((p) => selectedSet.has(p.id));

  const recommended = recommendMenuPerSlide(selectedSet.size);
  const effectivePerSlide = draft.menuPerSlide ?? recommended;

  const toggle = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const ids = Array.from(next);
    const updates: Partial<SignageDraftBase> = { productIds: ids };
    if (!perSlideTouched) {
      updates.menuPerSlide = recommendMenuPerSlide(ids.length);
    }
    onChange({ ...draft, ...updates } as SignageDraftBase);
  };

  return (
    <div className="space-y-3">
      {/* Title + search */}
      <div className="grid gap-2 md:grid-cols-2">
        <Input
          value={draft.menuTitle ?? ""}
          onChange={(e) => onChange({ ...draft, menuTitle: e.target.value })}
          placeholder="Título del board (opcional)"
        />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Buscar producto…"
        />
      </div>

      {/* Selected chips */}
      {selectedList.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selectedList.map((p) => (
            <Badge
              key={p.id}
              variant="secondary"
              className="cursor-pointer"
              onClick={() => toggle(p.id)}
              title="Quitar"
            >
              {p.name}
              <X className="ml-1 h-3 w-3" />
            </Badge>
          ))}
        </div>
      ) : null}

      {/* Product list */}
      <div className="max-h-56 overflow-y-auto rounded-md border">
        <ul className="divide-y">
          {filtered.slice(0, 200).map((p) => {
            const price = p.precioUnitario ?? p.precioPorKg ?? null;
            const isSelected = selectedSet.has(p.id);
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => toggle(p.id)}
                  className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-muted/60 ${isSelected ? "bg-primary/5" : ""}`}
                >
                  <input type="checkbox" checked={isSelected} readOnly className="pointer-events-none" />
                  {p.imagenUrl ? (
                    <img
                      src={p.imagenUrl}
                      alt=""
                      className="h-8 w-8 rounded object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded bg-muted" />
                  )}
                  <span className="flex-1 truncate">{p.name}</span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {price != null
                      ? `$${price.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`
                      : "—"}
                    {p.precioPorKg != null && p.precioUnitario == null ? "/kg" : ""}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <p className="text-xs text-muted-foreground">
        {selectedSet.size} de {products.length} productos seleccionados (máx 30).
      </p>

      {/* ── Presentation config panel ── */}
      {selectedSet.size > 0 && (
        <div className="space-y-3 rounded-md border bg-muted/20 p-3">
          <p className="text-xs font-semibold uppercase text-muted-foreground">
            Presentación en pantalla
          </p>

          {/* Per-slide selector */}
          <div className="space-y-1">
            <label className="text-xs font-medium">Productos por slide</label>
            <Select
              value={String(effectivePerSlide)}
              onValueChange={(v) => {
                const n = Number(v) as SignageMenuPerSlide;
                setPerSlideTouched(true);
                onChange({ ...draft, menuPerSlide: n });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PER_SLIDE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              Recomendado: {recommended} — {recommended === 1 ? "imagen a pantalla completa" : recommended === 2 ? "cada imagen ocupa media pantalla" : "tercios de pantalla"}
            </p>
          </div>

          {/* Toggles row */}
          <div className="grid gap-x-4 gap-y-2 md:grid-cols-2">
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={draft.menuShowBoardTitle ?? true}
                onChange={(e) => onChange({ ...draft, menuShowBoardTitle: e.target.checked })}
              />
              Mostrar título del board
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={draft.menuShowName ?? true}
                onChange={(e) => onChange({ ...draft, menuShowName: e.target.checked })}
              />
              Mostrar nombre del producto
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={draft.menuShowPrice ?? true}
                onChange={(e) => onChange({ ...draft, menuShowPrice: e.target.checked })}
              />
              Mostrar precio
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={draft.menuPriceMancheta ?? true}
                onChange={(e) => onChange({ ...draft, menuPriceMancheta: e.target.checked })}
              />
              Mancheta de precio (fondo resaltado)
            </label>
          </div>

          {/* Name position */}
          <div className="space-y-1">
            <label className="text-xs font-medium">Posición nombre/precio</label>
            <div className="flex gap-3 text-xs">
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  name="namePos"
                  checked={(draft.menuNamePosition ?? "bottom") === "bottom"}
                  onChange={() => onChange({ ...draft, menuNamePosition: "bottom" })}
                />
                Abajo
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  name="namePos"
                  checked={draft.menuNamePosition === "top"}
                  onChange={() => onChange({ ...draft, menuNamePosition: "top" })}
                />
                Arriba
              </label>
            </div>
          </div>

          {/* Layout fallback (advanced) */}
          <details open={advancedOpen} onToggle={(e) => setAdvancedOpen((e.target as HTMLDetailsElement).open)}>
            <summary className="cursor-pointer text-xs text-muted-foreground">
              Avanzado
            </summary>
            <div className="mt-2 grid gap-2 md:grid-cols-3">
              <label className="text-xs">
                Layout fallback
                <Select
                  value={draft.menuLayout ?? "grid"}
                  onValueChange={(v) =>
                    onChange({ ...draft, menuLayout: v === "list" ? "list" : "grid" })
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grid">Grilla (full-bleed)</SelectItem>
                    <SelectItem value="list">Lista compacta</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <label className="text-xs">
                Dwell por slide (ms)
                <Input
                  type="number"
                  min={1000}
                  max={120000}
                  step={500}
                  className="mt-1"
                  value={draft.menuSlideDwellMs ?? 7000}
                  onChange={(e) => onChange({ ...draft, menuSlideDwellMs: Number(e.target.value) || 7000 })}
                />
              </label>
              <label className="text-xs">
                Rotación imagen (ms)
                <Input
                  type="number"
                  min={500}
                  max={30000}
                  step={500}
                  className="mt-1"
                  value={draft.menuImageRotateMs ?? 4000}
                  onChange={(e) => onChange({ ...draft, menuImageRotateMs: Number(e.target.value) || 4000 })}
                />
              </label>
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

function PromoPicker({
  promotions,
  selectedId,
  onChange,
}: {
  promotions: PromotionLite[] | null;
  selectedId: string | null;
  onChange: (id: string | null) => void;
}) {
  if (promotions === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando promociones…
      </p>
    );
  }
  if (!promotions.length) {
    return (
      <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
        <p>No hay promociones creadas.</p>
        <Button variant="outline" size="sm" className="mt-3" asChild>
          <a href="/app/promociones">Crear promoción</a>
        </Button>
      </div>
    );
  }
  return (
    <Select value={selectedId ?? ""} onValueChange={(v) => onChange(v || null)}>
      <SelectTrigger>
        <SelectValue placeholder="Elegir promoción" />
      </SelectTrigger>
      <SelectContent>
        {promotions.map((p) => (
          <SelectItem key={p.id} value={p.id}>
            {p.bannerTitle?.trim() || p.name}
            {p.active ? "" : " (inactiva)"}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function CouponPicker({
  coupons,
  selectedId,
  onChange,
}: {
  coupons: CouponLite[] | null;
  selectedId: string | null;
  onChange: (id: string | null) => void;
}) {
  if (coupons === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando cupones…
      </p>
    );
  }
  if (!coupons.length) {
    return (
      <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
        <p>No hay cupones creados.</p>
        <Button variant="outline" size="sm" className="mt-3" asChild>
          <a href="/app/cupones">Crear cupón</a>
        </Button>
      </div>
    );
  }
  return (
    <Select value={selectedId ?? ""} onValueChange={(v) => onChange(v || null)}>
      <SelectTrigger>
        <SelectValue placeholder="Elegir cupón" />
      </SelectTrigger>
      <SelectContent>
        {coupons.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.code} · {c.name}
            {c.active ? "" : " (inactivo)"}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─── Playlist editor ─────────────────────────────────────────────────────────

const PLAYLIST_TRANSITION_TYPES: SignageTransition["type"][] = ["none", "fade", "slide"];

function PlaylistEditor({
  current,
  products,
  promotions,
  coupons,
  media,
  onApply,
}: {
  current: SignageContent;
  products: ProductLite[] | null;
  promotions: PromotionLite[] | null;
  coupons: CouponLite[] | null;
  media: MediaLite[] | null;
  onApply: (draft: SignageDraft) => void;
}) {
  const slideKinds = EDITOR_MODES.filter((m) => m.value !== "playlist");

  const initialSlides: SignageDraftBase[] =
    current.kind === "playlist"
      ? current.slides.map((s) => baseFromContentBase(s))
      : [
          { mode: "menu", productIds: [], menuTitle: "", menuLayout: "grid" },
          { mode: "promo", promotionId: null },
        ];

  const [slides, setSlides] = useState<SignageDraftBase[]>(initialSlides);
  const [dwellMs, setDwellMs] = useState<number>(
    current.kind === "playlist" ? current.dwellMs : 8000,
  );
  const [transition, setTransition] = useState<SignageTransition>(
    current.kind === "playlist" ? current.transition : { type: "fade", ms: 400 },
  );

  const updateSlide = (idx: number, next: SignageDraftBase) => {
    setSlides((prev) => prev.map((s, i) => (i === idx ? next : s)));
  };
  const removeSlide = (idx: number) => {
    setSlides((prev) => prev.filter((_, i) => i !== idx));
  };
  const addSlide = () => {
    setSlides((prev) => [...prev, { mode: "image", mediaId: null, imageAlt: "" }]);
  };

  const canApply = slides.length >= 2 && slides.every(draftIsApplyable);

  const apply = () => {
    const draft: SignageDraftPlaylist = {
      mode: "playlist",
      dwellMs,
      transition,
      slides,
    };
    onApply(draft);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
        <p className="font-medium">Playlist = rotación de contenidos distintos</p>
        <p className="mt-0.5 text-muted-foreground">
          Cada slide puede ser menú, promoción, cupón, imagen, URL o texto. No es una
          presentación de solo mensajes: elegí el tipo en cada slide.
        </p>
      </div>

      <div className="grid gap-2 md:grid-cols-3">
        <label className="text-xs">
          Dwell por slide (ms)
          <Input
            type="number"
            min={2000}
            max={120000}
            step={500}
            value={dwellMs}
            onChange={(e) => setDwellMs(Number(e.target.value) || 8000)}
          />
        </label>
        <label className="text-xs">
          Transición
          <Select
            value={transition.type}
            onValueChange={(v) =>
              setTransition({
                type: (v as SignageTransition["type"]) ?? "fade",
                ms: transition.ms,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PLAYLIST_TRANSITION_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t === "none" ? "Sin transición" : t === "fade" ? "Fundido" : "Deslizar"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="text-xs">
          Duración transición (ms)
          <Input
            type="number"
            min={50}
            max={2000}
            step={50}
            value={transition.ms ?? 400}
            onChange={(e) =>
              setTransition({
                type: transition.type,
                ms: Number(e.target.value) || 400,
              })
            }
            disabled={transition.type === "none"}
          />
        </label>
      </div>

      <div className="space-y-3">
        {slides.map((slide, idx) => (
          <div key={idx} className="rounded-md border bg-muted/20 p-3">
            <div className="mb-3 flex items-start justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">
                  Slide {idx + 1}
                </p>
                <p className="text-[11px] text-muted-foreground">Tipo de contenido</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeSlide(idx)}
                disabled={slides.length <= 2}
                title={slides.length <= 2 ? "La playlist requiere al menos 2 slides" : "Eliminar slide"}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {slideKinds.map((m) => {
                const active = slide.mode === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() =>
                      updateSlide(
                        idx,
                        baseInitialState(m.value as Exclude<EditorMode, "playlist">, {
                          kind: "idle",
                        }),
                      )
                    }
                    className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                      active
                        ? "border-primary bg-primary/10 font-medium text-primary"
                        : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                    }`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            <BaseEditorFields
              draft={slide}
              onChange={(next) => updateSlide(idx, next)}
              products={products}
              promotions={promotions}
              coupons={coupons}
              media={media}
              fieldId={`slide-${idx}`}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={addSlide} disabled={slides.length >= 20}>
          <Plus className="mr-1.5 h-4 w-4" />
          Agregar slide
        </Button>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            {slides.length} slides · min 2 · max 20
          </span>
          <Button size="sm" onClick={apply} disabled={!canApply}>
            <Check className="mr-1.5 h-4 w-4" />
            Aplicar playlist
          </Button>
        </div>
      </div>
    </div>
  );
}

function baseFromContentBase(base: import("@/lib/db-service/signage").SignageContentBase): SignageDraftBase {
  switch (base.kind) {
    case "idle":
      return { mode: "idle" };
    case "text":
      return { mode: "text", text: base.text, theme: base.theme };
    case "image":
      return { mode: "image", mediaId: null, imageAlt: base.alt ?? "" };
    case "url":
      return { mode: "url", externalUrl: base.url, urlRefreshMs: base.refreshMs ?? null };
    case "menu": {
      const p = base.presentation;
      return {
        mode: "menu",
        productIds: base.items.map((it) => it.id),
        menuTitle: base.title,
        menuLayout: base.layout,
        menuPerSlide: p?.perSlide,
        menuShowBoardTitle: p?.showBoardTitle,
        menuShowName: p?.showName,
        menuShowPrice: p?.showPrice,
        menuNamePosition: p?.namePosition,
        menuPriceMancheta: p?.priceMancheta,
        menuSlideDwellMs: p?.slideDwellMs,
        menuImageRotateMs: p?.imageRotateMs,
      };
    }
    case "promo":
      return { mode: "promo", promotionId: base.item.id };
    case "coupon":
      return { mode: "coupon", couponId: base.item.id };
    default:
      return { mode: "idle" };
  }
}

// ─── SCRUM-135: Schedule Section ─────────────────────────────────────────────

const DAY_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6] as const;

type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

function formatDays(days: DayOfWeek[]): string {
  if (days.length === 7) return "Todos los días";
  if (days.length === 0) return "Ningún día";
  const sorted = [...days].sort();
  // Lun-Vie
  if (JSON.stringify(sorted) === JSON.stringify([1, 2, 3, 4, 5])) return "Lun-Vie";
  if (JSON.stringify(sorted) === JSON.stringify([0, 6])) return "Sáb-Dom";
  return sorted.map((d) => DAY_LABELS[d]).join(", ");
}

function ScheduleSection({
  displayId,
  tenantId,
}: {
  displayId: string;
  tenantId: string;
}) {
  const [rules, setRules] = useState<SignageScheduleRule[]>([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [fLabel, setFLabel] = useState("");
  const [fStart, setFStart] = useState("09:00");
  const [fEnd, setFEnd] = useState("18:00");
  const [fTimezone, setFTimezone] = useState("America/Argentina/Buenos_Aires");
  const [fPriority, setFPriority] = useState(10);
  const [fDays, setFDays] = useState<DayOfWeek[]>([0, 1, 2, 3, 4, 5, 6]);
  const [fContent, setFContent] = useState("idle");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      const res = await apiGetJson<{ items: SignageScheduleRule[] }>(
        `/api/signage/schedules?displayId=${encodeURIComponent(displayId)}`,
        tenantId,
      );
      setRules(res.items ?? []);
    } catch {
      toast.error("Error al cargar reglas de schedule");
    } finally {
      setLoadingRules(false);
    }
  }, [displayId, tenantId]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  const toggleDay = (d: DayOfWeek) => {
    setFDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
    );
  };

  const handleCreate = async () => {
    if (!fLabel.trim() || fDays.length === 0) {
      toast.error("Completá etiqueta y al menos un día");
      return;
    }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        displayId,
        label: fLabel.trim(),
        daysOfWeek: fDays,
        startTime: fStart,
        endTime: fEnd,
        timezone: fTimezone,
        priority: fPriority,
        // Draft mínimo: contenido idle como placeholder hasta publicar
        draft: { mode: fContent },
      };
      await apiSendJson("/api/signage/schedules", "POST", body, tenantId);
      toast.success("Regla creada (borrador)");
      setShowForm(false);
      setFLabel("");
      setFDays([0, 1, 2, 3, 4, 5, 6]);
      void loadRules();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al crear regla");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (rule: SignageScheduleRule, force = false) => {
    setPublishing(rule.id);
    try {
      await apiSendJson(
        "/api/signage/schedules",
        "PATCH",
        { id: rule.id, action: "publish", force },
        tenantId,
      );
      toast.success("Regla publicada");
      void loadRules();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // Detect overlap_warning from server (409 response body)
      if (msg.includes("overlap") || msg.includes("solapamiento")) {
        toast.error(
          "Hay solapamiento de prioridad. Cambiá la prioridad o confirmá para forzar.",
        );
      } else {
        toast.error(msg || "Error al publicar");
      }
    } finally {
      setPublishing(null);
    }
  };

  const handleUnpublish = async (rule: SignageScheduleRule) => {
    setPublishing(rule.id);
    try {
      await apiSendJson(
        "/api/signage/schedules",
        "PATCH",
        { id: rule.id, action: "unpublish" },
        tenantId,
      );
      toast.success("Regla vuelve a borrador");
      void loadRules();
    } catch {
      toast.error("Error al despublicar");
    } finally {
      setPublishing(null);
    }
  };

  const handleDelete = async (rule: SignageScheduleRule) => {
    setDeleting(rule.id);
    try {
      const url = `/api/signage/schedules?id=${encodeURIComponent(rule.id)}`;
      await apiSendJson(url, "DELETE", undefined, tenantId);
      toast.success("Regla eliminada");
      void loadRules();
    } catch {
      toast.error("Error al eliminar");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="mb-3 flex items-center gap-2">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <p className="text-xs font-semibold uppercase text-muted-foreground">
          Programación por horario
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-7 text-xs"
          onClick={() => setShowForm((v) => !v)}
        >
          <Plus className="mr-1 h-3 w-3" />
          Nueva regla
        </Button>
      </div>

      {/* Formulario nueva regla */}
      {showForm && (
        <div className="mb-4 space-y-3 rounded-md border bg-background p-3">
          <p className="text-xs font-semibold">Nueva regla de horario (borrador)</p>

          <label className="block text-xs">
            Etiqueta (ej: "Happy hour 18-20h")
            <Input
              className="mt-1"
              value={fLabel}
              onChange={(e) => setFLabel(e.target.value)}
              placeholder="Nombre de la regla"
              maxLength={200}
            />
          </label>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <label className="text-xs">
              Hora inicio
              <Input
                className="mt-1"
                type="time"
                value={fStart}
                onChange={(e) => setFStart(e.target.value)}
              />
            </label>
            <label className="text-xs">
              Hora fin
              <Input
                className="mt-1"
                type="time"
                value={fEnd}
                onChange={(e) => setFEnd(e.target.value)}
              />
            </label>
            <label className="text-xs">
              Prioridad (mayor = gana)
              <Input
                className="mt-1"
                type="number"
                min={0}
                max={9999}
                value={fPriority}
                onChange={(e) => setFPriority(Number(e.target.value) || 10)}
              />
            </label>
            <label className="text-xs">
              Contenido (draft)
              <Select value={fContent} onValueChange={setFContent}>
                <SelectTrigger className="mt-1 h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="idle">Idle (pantalla vacía)</SelectItem>
                  <SelectItem value="text">Texto</SelectItem>
                  <SelectItem value="image">Imagen</SelectItem>
                  <SelectItem value="menu">Menú</SelectItem>
                  <SelectItem value="promo">Promoción</SelectItem>
                  <SelectItem value="coupon">Cupón</SelectItem>
                  <SelectItem value="playlist">Playlist</SelectItem>
                </SelectContent>
              </Select>
            </label>
          </div>

          <div>
            <p className="mb-1 text-xs font-medium">Días activos</p>
            <div className="flex flex-wrap gap-1">
              {ALL_DAYS.map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant={fDays.includes(d) ? "default" : "outline"}
                  className="h-7 w-10 text-xs"
                  onClick={() => toggleDay(d)}
                >
                  {DAY_LABELS[d]}
                </Button>
              ))}
            </div>
          </div>

          <label className="block text-xs">
            Timezone
            <Input
              className="mt-1"
              value={fTimezone}
              onChange={(e) => setFTimezone(e.target.value)}
              placeholder="America/Argentina/Buenos_Aires"
            />
          </label>

          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreate} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-1.5 h-4 w-4" />
              )}
              Crear borrador
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowForm(false)}
            >
              Cancelar
            </Button>
          </div>

          <div className="rounded-md border border-amber-200/60 bg-amber-50/50 p-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
            <AlertTriangle className="mr-1 inline h-3 w-3" />
            <strong>Borrador:</strong> la regla no aplica al player hasta que la publiques.
            Al publicar, el server resuelve el contenido (snapshot cross-tenant seguro).
            Si hay solapamiento de prioridad exacta, el server avisa — podés forzar o cambiar prioridad.
          </div>
        </div>
      )}

      {/* SCRUM-140: Temporal preview ────────────────────────────────────── */}
      <TemporalPreview rules={rules} />

      {/* Lista de reglas */}
      {loadingRules ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Cargando reglas…
        </p>
      ) : rules.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Sin reglas de programación. El display usa el contenido estático asignado.
        </p>
      ) : (
        <div className="space-y-2">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className="flex flex-wrap items-center gap-2 rounded-md border bg-background px-3 py-2 text-xs"
            >
              <CalendarDays className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="font-medium">{rule.label}</span>
              <span className="text-muted-foreground">
                {rule.startTime} – {rule.endTime}
              </span>
              <span className="text-muted-foreground">
                {formatDays(rule.daysOfWeek as DayOfWeek[])}
              </span>
              <Badge
                variant="outline"
                className="text-[10px]"
              >
                P:{rule.priority}
              </Badge>
              {rule.content && (
                <Badge variant="outline" className="text-[10px]">
                  {rule.content.kind}
                </Badge>
              )}
              <Badge
                className={
                  rule.status === "published"
                    ? "bg-green-100 text-green-800 text-[10px]"
                    : "bg-yellow-100 text-yellow-800 text-[10px]"
                }
              >
                {rule.status === "published" ? "Publicada" : "Borrador"}
              </Badge>

              <div className="ml-auto flex items-center gap-1">
                {rule.status === "draft" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 text-[10px]"
                    disabled={publishing === rule.id}
                    onClick={() => void handlePublish(rule)}
                  >
                    {publishing === rule.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      "Publicar"
                    )}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-[10px] text-muted-foreground"
                    disabled={publishing === rule.id}
                    onClick={() => void handleUnpublish(rule)}
                  >
                    {publishing === rule.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      "Despublicar"
                    )}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-destructive"
                  disabled={deleting === rule.id}
                  onClick={() => void handleDelete(rule)}
                  title="Eliminar regla"
                >
                  {deleting === rule.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>
          ))}
          <p className="text-[10px] text-muted-foreground">
            Mayor prioridad gana ante solapamiento.
            Fallback: contenido estático del display si ninguna regla aplica.
          </p>
        </div>
      )}
    </div>
  );
}
